import { useEffect, useRef, useState } from 'react';
import { useShallow } from 'zustand/react/shallow';
import useWidth from '../../../../components/Hooks/useWidth';
import useElementSize from '../../../../hooks/useElementSize';
import { useModalState } from '../../../../hooks/useModalState';
import useOnNavigate from '../../../../hooks/useOnNavigate';
import { useSuggestedProducts } from '../../../../services/modules/products/products.queries';
import useProductDetailsStore from '../../../../stores/ProductDetails/productDetails.store';
import { useApplicationContext } from '../../../context/ApplicationContext/ApplicationContext';
import { UsedInOfflineMode } from '../../../offline/types/UsedInOfflineMode';
import useOrderBook from '../../../order-book/hooks/useOrderBook';
import useSharedEntities from '../../../shared-entities/hooks/useSharedEntities';
import { useAbility } from '../../../users/AbilityContext';
import useGalleryColumnWidth from '../../details/gallery/hooks/useGalleryColumnWidth';
import { ProductDetailsEntityType } from '../types/productDetails.types';
import { mapEntityTypeMountPropsToChildren } from '../utils/mountProductDetailsContentProps';
import {
  getAllProductColorIds,
  resolveSuggestedProductsCustomers,
} from '../utils/productDetailsContent.utils';
import useFindDetailsEntity from './useFindDetailsEntity';
import useKitDetailsMedias from './useKitDetailsMedias';
import useProductDetailsMassActions from './useProductDetailsMassActions';
import useProductDetailsMedias from './useProductDetailsMedias';
import useProductDetailsSections, { ProductDetailsSection } from './useProductDetailsSections';
import useProductDetailsShortcuts from './useProductDetailsShortcuts';
import useProductDetailsActions from './useProductsDetailsActions';
import useScrollAnchor from './useScrollAnchor';
import useSyncProductDetailsOrders from './useSyncProductDetailsOrders';

const useProductDetailsContent = ({ isOfflineMode = false }: UsedInOfflineMode) => {
  const [scrollElement, setScrollElement] = useState<HTMLDivElement | null>(null);
  const [titleElement, setTitleElement] = useState<HTMLDivElement | null>(null);
  const [mediaElement, setMediaElement] = useState<HTMLDivElement | null>(null);
  const [panelElement, setPanelElement] = useState<HTMLDivElement | null>(null);
  const [listElement, setListElement] = useState<HTMLDivElement | null>(null);

  const {
    copiedGrid,
    productGridParams,
    pasteGridMassAction,
    isMobileHeaderFixed,
    productDetailsModalState,
    setOpenedByOrderBook,
  } = useProductDetailsStore(
    useShallow((state) => ({
      copiedGrid: state.copiedGrid,
      productGridParams: state.productGridParams,
      pasteGridMassAction: state.pasteGridMassAction,
      isMobileHeaderFixed: state.isMobileHeaderFixed,
      productDetailsModalState: state.productDetailsModalState,
      setOpenedByOrderBook: state.setOpenedByOrderBook,
    })),
  );

  const { width: mediaWidth, height: mediaHeight } = useElementSize(mediaElement);
  const { width: panelWidth } = useElementSize(panelElement);
  const { width: listWidth } = useElementSize(listElement);
  const galleryColumnWidth = useGalleryColumnWidth({ galleryHeight: mediaHeight, panelWidth });
  const { mediumDownWidth: isStackedLayout } = useWidth();

  const filtersRowRef = useRef<HTMLDivElement | null>(null);
  const { isSectionOpen, toggleSection } = useProductDetailsSections();
  const isGridOpen = isSectionOpen(ProductDetailsSection.GRID);
  const { captureAnchor: captureGridAnchor } = useScrollAnchor({
    anchorRef: filtersRowRef,
    scrollElement,
  });

  const toggleGrid = () => {
    captureGridAnchor();
    toggleSection(ProductDetailsSection.GRID);
  };

  const { entity, isLoading } = useFindDetailsEntity();
  const { zoomIn, zoomOut, zoomMedia, handleCloseModal, focusZoomPercentToScroll } =
    useProductDetailsActions();

  useOnNavigate(handleCloseModal);

  const { isStore } = useApplicationContext();
  const { customerIds } = useOrderBook();
  const { isPublicAccess } = useSharedEntities({});
  const ability = useAbility();

  const {
    isOpen: isPasteGridConfirmModalOpen,
    closeModal: closePasteGridConfirmModal,
    openModal: openPasteGridConfirmModal,
  } = useModalState();

  const {
    totalItems,
    totalSelected,
    canPasteGrid,
    batchPasteGrid,
    isLoadingPasteGridMassAction,
    massActionBlockReasonMessage,
    toggleSelectAllItemsMassAction,
    deselectAllItemsMassAction,
  } = useProductDetailsMassActions({
    entity,
    isOfflineMode,
    selectedProductColorId: entity?.type === 'PRODUCT' ? entity.data.productColorId : undefined,
    onSuccessPasteGrid: () => {
      closePasteGridConfirmModal();
    },
  });

  const mappedProps = mapEntityTypeMountPropsToChildren(entity);

  const allProductColorIds = getAllProductColorIds(entity);
  const isProductEntity = entity?.type === ProductDetailsEntityType.PRODUCT;
  const { medias: productColorMedias, isLoading: isLoadingMedias } = useProductDetailsMedias();
  const kitMedias = useKitDetailsMedias(mappedProps?.medias ?? []);
  const galleryMedias = isProductEntity ? productColorMedias : kitMedias;

  const { customerIds: requestCustomerIds, hasCustomer } = resolveSuggestedProductsCustomers({
    isStore,
    customerIds,
    modalCustomerId: productDetailsModalState.customerId,
  });

  const { data: suggestedProductsData } = useSuggestedProducts(
    {
      customerIds: requestCustomerIds,
      productIds: isProductEntity ? [entity.data.productId] : [],
    },
    {
      enabled: isProductEntity && hasCustomer,
    },
  );

  const disableGrid =
    productGridParams.isFinishedOrder ||
    productDetailsModalState.disableGrid ||
    ability.cannot('update', 'orders') ||
    isPublicAccess;

  useEffect(() => {
    setOpenedByOrderBook({ state: false });
  }, [setOpenedByOrderBook]);

  useProductDetailsShortcuts({
    isEnabled: !zoomMedia && !isPasteGridConfirmModalOpen,
    onClose: handleCloseModal,
  });

  useSyncProductDetailsOrders();

  return {
    entity,
    zoomIn,
    zoomOut,
    zoomMedia,
    isLoading,
    galleryMedias,
    isLoadingMedias,
    setMediaElement,
    setListElement,
    listWidth,
    scrollElement,
    setScrollElement,
    copiedGrid,
    totalItems,
    mediaWidth,
    mediaHeight,
    galleryColumnWidth,
    setPanelElement,
    isStackedLayout,
    mappedProps,
    disableGrid,
    canPasteGrid,
    totalSelected,
    batchPasteGrid,
    isGridOpen,
    toggleGrid,
    handleCloseModal,
    filtersRowRef,
    isSectionOpen,
    toggleSection,
    isMobileHeaderFixed,
    titleElement,
    setTitleElement,
    allProductColorIds,
    pasteGridMassAction,
    focusZoomPercentToScroll,
    openPasteGridConfirmModal,
    closePasteGridConfirmModal,
    isPasteGridConfirmModalOpen,
    massActionBlockReasonMessage,
    isLoadingPasteGridMassAction,
    toggleSelectAllItemsMassAction,
    deselectAllItemsMassAction,
    suggestedProducts: suggestedProductsData?.suggestedProducts ?? [],
  };
};

export default useProductDetailsContent;
